import {
  OreButton,
  OrePagination,
  OrePill,
  OreStack,
  OreTable,
  OreTableBody,
  OreTableCell,
  OreTableContainer,
  OreTableHead,
  OreTableRow,
  OreText,
  SvgDownload,
  SvgPlus,
  SvgInfo
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useUserData } from '@/User/ui/context'
import { SITE_INFO } from '@/Shared/constants/siteInfo'
import { TableHeadersModel } from '@/Shared/domain/models/TableHeadersModel'
import { OreMultiSelectOption } from '@/Shared/ui/molecules/OreMultiSelect'
import { tablePagination } from '@/Shared/ui/sharedTranslation'
import { useTpv } from '@/Store/ui/context'
import { AddTpvModalActions, TpvFilter } from '@/Store/ui/molecules'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { useNavigate } from 'react-router-dom'
import { navigationRoutes } from '@/Shared/ui/routers'
import { EllipsisTextTooltip } from '@/Shared/ui/ellipsisTextTooltip'

const ROWS_PER_PAGE = 8
interface TpvsListProps {
  tpvModalRef: React.RefObject<AddTpvModalActions>
}

export const TpvsList = ({ tpvModalRef }: TpvsListProps): JSX.Element => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { model, count, loadTpvs, downloadTpvsDataExcel, filters, setFilters } = useTpv()
  const { merchant } = useUserData()

  const [page, setPage] = useState<number>(1)
  const [hasTpvs, setHasTpvs] = useState<boolean>(false)
  const [hasPendingToActivateTpvs, setHasPendingToActivateTpvs] = useState<boolean>(false)

  const handleAddTpvClick = () => {
    if (merchant?.isIndependent) {
      tpvModalRef?.current?.open()
    }
    else {
      navigate(navigationRoutes.tpvsExcelUpload)
    }
  }


  useEffect(() => {
    loadTpvs(page, filters)
  }, [page, filters])

  useEffect(() => {
    if (model.length && !filters.multiFieldFileter && !filters.merchantTargetList.length) {
      setHasTpvs(true)
    }
    setHasPendingToActivateTpvs(model.some(item => item.isPedingActivation))

  }, [model, filters])


  
  const headers: TableHeadersModel[] = [
    {
      key: 'affiliateCode',
      label: storesTranslation.tpvs.list.table.affiliateCode,
      size: 'quarter',
    },
    { key: 'local', label: storesTranslation.tpvs.list.table.local, size: 'quarter' },
    {
      key: 'fullAddress',
      label: storesTranslation.tpvs.list.table.address,
      size: 'quarter',
    },
    {
      key: 'commerceCode',
      label: storesTranslation.tpvs.list.table.commerceCode,
      size: 'quarter',
    },
  ]

  const [localOptions, setLocalOptions] = useState<OreMultiSelectOption[]>([])

  useEffect(() => {
    if (filters.multiFieldFileter) return

    setLocalOptions(previous => {
      const localsByCode = new Map(previous.map(option => [option.value, option.label]))
      model.forEach(item => {
        if (item.local && item.affiliateCode && !localsByCode.has(item.affiliateCode)) {
          localsByCode.set(item.affiliateCode, item.local)
        }
      })
      return Array.from(localsByCode, ([value, label]) => ({ value, label }))
    })
  }, [model, filters.multiFieldFileter])

  const handleSearch = (searchTerm: string, localCodes: string[]): void => {
    setFilters({
      multiFieldFileter: searchTerm,
      merchantTargetList: localCodes.map(Number)
    })
    setPage(1)
  }

  const handleClear = (searchTerm: string): void => {
    setFilters({
      multiFieldFileter: searchTerm,
      merchantTargetList: []
    })
    setPage(1)
  }

  return (
    <OreStack placeContent="stretch" space="large">
      <OreTableContainer>
        {hasTpvs ?
          <TpvFilter
            localOptions={localOptions}
            onSearch={handleSearch}
            onClear={handleClear}
            isOpen={true}
          /> : null}
        <OreTable>
          <OreTableHead valign="middle">
            <OreTableRow hover={false}>
              {headers.map(header => (
                <OreTableCell as="th" key={header.key} size={header.size}>
                  <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                    {header.label && t(header.label)}
                  </span>
                </OreTableCell>
              ))}
            </OreTableRow>
          </OreTableHead>
          <OreTableBody valign="top">
            {model.length ? (
              model.map(item => (
                <OreTableRow key={item.terminalMid}>
                  <OreTableCell>
                    <OreText size="text-sm" tone={item?.isPedingActivation ? 'neutral-400' : 'neutral-600'} bold>
                      {item.affiliateCode}
                    </OreText>
                  </OreTableCell>
                  <OreTableCell>
                    <OreText size="text-sm" tone={item?.isPedingActivation ? 'neutral-400' : 'neutral-600'}>
                      <EllipsisTextTooltip text={item.local} disabled={item?.isPedingActivation} />
                    </OreText>
                  </OreTableCell>
                  <OreTableCell >
                    <OreText size="text-sm" tone={item?.isPedingActivation ? 'neutral-400' : 'neutral-600'}>
                      <EllipsisTextTooltip text={item.fullAddress} disabled={item?.isPedingActivation} />
                    </OreText>
                  </OreTableCell>
                  <OreTableCell>
                    <OreStack direction="column" space="2xsmall">
                      <OreText
                        size="text-sm"
                        tone={item?.isPedingActivation ? 'neutral-400' : 'neutral-600'}
                      >
                        {item.commerceCode}
                      </OreText>
                      {item?.isPedingActivation && (
                        <OrePill tone="grey">{t(storesTranslation.tpvs.list.inactiveLabel)}</OrePill>
                      )}
                    </OreStack>
                  </OreTableCell>
                </OreTableRow>
              ))
            ) : (
              <OreTableRow valign="middle">
                <OreTableCell colSpan={headers.length}>
                  <OreText size="text-md" data-testid="noTpvs">
                    {hasTpvs ?
                      t(storesTranslation.tpvs.list.noTpvs) :
                      t(storesTranslation.tpvs.list.noTpvsTableText)}
                  </OreText>
                </OreTableCell>
              </OreTableRow>
            )}
          </OreTableBody>
        </OreTable>
        {count > 0 ? (
          <OrePagination
            displayPageButtons
            count={count}
            labelNextPage={t(tablePagination.nextPageLabel)}
            labelPreviousPage={t(tablePagination.previousPageLabel)}
            onPageChange={setPage}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            text={t(tablePagination.pageOf)}
            legend={t(tablePagination.rowsShowing)}
          />
        ) : null}
      </OreTableContainer>

      <OreStack direction="column" placeContent="stretch" placeItems="stretch">


        {!model.length ? (
          <OreStack direction="column" placeContent="start" placeItems="start" space="2xsmall" style={{ marginBottom: "24px" }}>
            <OreText size="text-lg" bold style={{ fontSize: "20px" }}>{hasTpvs ? t(storesTranslation.tpvs.list.noResultsTitle) : t(storesTranslation.tpvs.list.noTpvsTitle)}</OreText>
            <OreText size="text-md" tone="neutral" style={{ marginBottom: "12px" }}>
              {hasTpvs ? t(storesTranslation.tpvs.list.noResultsText) : t(storesTranslation.tpvs.list.noTpvsText)}
            </OreText>
            <OreButton
              category="highlight"
              icon={<SvgPlus />}
              onClick={handleAddTpvClick}
            >
              {t(storesTranslation.tpvs.list.add)}
            </OreButton>
          </OreStack>
        ) : null}
        {hasTpvs ? (
          <OreStack direction="column" placeContent="stretch" placeItems="stretch" space="medium">
           {hasPendingToActivateTpvs && (
             <OreStack direction="row" placeItems="center"
              style={{
                backgroundColor: "#F1F5F9",
                borderRadius: "8px",
                border: "1px solid #CBD5E1",
                padding: "1rem"
              }}>
              <OreStack
                direction="row"
                placeItems="center"
                style={{
                  paddingRight: "8px"
                }}>
                <SvgInfo />
                <OreText tone="neutral-600" >
                  <Trans t={t}>
                    {t(storesTranslation.tpvs.list.infoMessage)}
                  </Trans>
                </OreText>
              </OreStack>
            </OreStack>
           )}
            <OreText size="text-md" tone="neutral">
              <Trans
                t={t}
                components={[
                  <a
                    key="contact-email"
                    href={`mailto:${SITE_INFO.contact.email}?subject=${t(storesTranslation.tpvs.list.footerMailSubject)}`}
                    aria-label={SITE_INFO.contact.email}
                  >
                    {SITE_INFO.contact.email}
                  </a>,
                ]}
              >
                {t(storesTranslation.tpvs.list.footer, {
                  CONTACT_EMAIL: SITE_INFO.contact.email,
                })}
              </Trans>
            </OreText>
            <OreStack direction="row" placeContent="end" placeItems="end">
              <OreButton
                onClick={downloadTpvsDataExcel}
                category="secondary"
                icon={<SvgDownload />}
                disabled={count <= 0}
              >
                {t(storesTranslation.tpvs.list.download)}
              </OreButton>
            </OreStack>
          </OreStack>
        ) : null}
      </OreStack>
    </OreStack>
  )
}
